<template>
  <div
    class="va-card"
    :class="{ 'locale-zh-CN': isZhLocale }"
    @click="$emit('click', item)"
  >
    <div class="cover-wrap">
      <img class="cover" :src="coverSrc" alt="" @error="onCoverError" />
      <span
        v-if="scoreText"
        class="score-badge"
        :style="scoreBadgeStyle"
      >{{ scoreText }}</span>
    </div>
    <div class="body">
      <h3 class="title" :title="item.title">{{ item.title || '' }}</h3>
      <div class="meta">
        <span class="meta-field" :title="displayField">{{ displayField }}</span>
        <span class="meta-date">{{ displayDate }}</span>
      </div>
      <p class="desc" :title="item.description">{{ displayDesc }}</p>
      <div class="footer">
        <span class="maturity">{{ displayMaturity }}</span>
        <span class="detail-link">
          {{ labels.viewDetail }}
          <img v-if="arrowIcon" class="arrow" :src="arrowIcon" alt="" @error="onImgError" />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { getValueAssessmentNewConfig } from '@/config/valueAssessmentNewConfig'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getCurrentLocale } from '@/utils/i18nText'

const EMPTY_TIP = '待接口提供'

export default {
  name: 'ValueAssessmentCard',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      coverFailed: false
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    config() {
      return getValueAssessmentNewConfig(getCurrentLocale())
    },
    labels() {
      return this.config.card || {}
    },
    emptyTip() {
      return this.labels.emptyTip || EMPTY_TIP
    },
    fallbackCover() {
      return getSafeImage(this.labels.coverFallback)
    },
    arrowIcon() {
      return getSafeImage(this.labels.arrowIcon)
    },
    scoreBadgeSrc() {
      return getSafeImage(this.labels.scoreBadge)
    },
    scoreBadgeStyle() {
      const src = this.scoreBadgeSrc
      if (!src) return null
      return {
        backgroundImage: `url(${src})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '100% 100%'
      }
    },
    coverSrc() {
      if (this.coverFailed) return this.fallbackCover
      const src = getSafeImage(this.item.cover)
      return src || this.fallbackCover
    },
    scoreText() {
      const score = this.item.score
      if (score === undefined || score === null || score === '') return ''
      const unit = this.labels.scoreUnit || '分'
      return `${score}${unit}`
    },
    displayField() {
      return this.normalizeText(this.item.field || this.item.type)
    },
    displayDate() {
      return this.normalizeText(this.item.date)
    },
    displayDesc() {
      return this.normalizeText(this.item.description)
    },
    displayMaturity() {
      return this.normalizeText(this.item.maturity)
    }
  },
  watch: {
    'item.cover'() {
      this.coverFailed = false
    },
    'item.id'() {
      this.coverFailed = false
    }
  },
  methods: {
    normalizeText(val) {
      const text = String(val == null ? '' : val).trim()
      if (!text || text === this.emptyTip || text === EMPTY_TIP) return ''
      return text
    },
    onCoverError(e) {
      this.coverFailed = true
      handleImgError(e)
    },
    onImgError(e) {
      handleImgError(e)
    }
  }
}
</script>

<style lang="scss" scoped>
.va-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);

    .title {
      color: #8c7c26;
    }

    .detail-link {
      color: #8c7c26;
    }
  }
}

.cover-wrap {
  position: relative;
  width: 100%;
  height: 168px;
  background: #f3f5f4;
  overflow: hidden;
}

.cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.score-badge {
  position: absolute;
  top: 10px;
  right: 0;
  min-width: 72px;
  height: 28px;
  padding: 0 14px 0 18px;
  border-radius: 14px 0 0 14px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.15) 0%, rgba(210, 230, 170, 0.92) 100%);
  color: #5a8a28;
  font-size: 13px;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
  box-sizing: border-box;
}

.body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 14px 16px 16px;
  box-sizing: border-box;
}

.title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #222;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 44px;
}

.meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e5e5e5;
  font-size: 13px;
}

.meta-field {
  flex: 1;
  min-width: 0;
  color: #a0903c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-date {
  flex-shrink: 0;
  color: #999;
}

.desc {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: #666;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 60px;
  flex: 1;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
}

.maturity {
  max-width: 62%;
  padding: 4px 10px;
  border-radius: 2px;
  background: rgba(64, 128, 200, 0.1);
  color: #3a78b8;
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.maturity-empty {
  background: rgba(0, 0, 0, 0.04);
  color: #999;
}

.detail-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 13px;
  color: #999;
}

.arrow {
  width: 10px;
  height: 10px;
  object-fit: contain;
}
.locale-zh-CN {
  .title {
    font-size: 18px;
  }

  .meta {
    font-size: 15px;
  }

  .desc {
    font-size: 15px;
  }

  .maturity {
    font-size: 13px;
  }

  .detail-link {
    font-size: 14px;
  }

  .score-badge {
    min-width: 80px;
    height: 30px;
    line-height: 30px;
    font-size: 14px;
  }

  .arrow {
    width: 12px;
    height: 12px;
  }
}
</style>
